import { fg } from '@atlaskit/platform-feature-flags';

import { fetchAgg } from '../../../../common/utils/fetch-agg';
import { generateHash } from '../../../../utils/caching/common/key-generator';
import {
	type CacheAnalyticsCallback,
	type NetworkCallbacks,
	promiseCache,
} from '../../../../utils/caching/persist-refresh';
import { SEARCH_COMBINED_CONFIG_CACHE_KEY } from '../../../../utils/caching/persist-refresh/constants';
import { DegradablePromise, type DegradationCallback } from '../../../utils/degradable-promise';

import { generateCombinedConfigHash as getCombinedConfigHash } from './hash';
import { getSearchConfigQuery, GQL_OPERATION_NAME } from './query';

export const SEARCH_COMBINED_CONFIG: 'search-combined-config' = SEARCH_COMBINED_CONFIG_CACHE_KEY;

type CombinedConfigQueryVariables = {
	cloudId: string;
};

export type SearchCombinedFirstPartyProduct = {
	name: string;
};

export type SearchCombinedThirdPartyProduct = {
	name: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
	providerId?: string | null;
	integrationARI?: string | null;
	workspaceName?: string | null;
	workspaceUrl?: string | null;
	datasourceId?: string | null;
	connectionId?: string | null;
	connectorSyncType?: string | null;
	isImpersonationAuthEnabled?: boolean | null;
	connectorCreatedAt?: string | null;
	connectedAt?: string | null;
};

export type SearchCombinedSiteProductInfo = {
	name?: string | null;
	dataRegion?: string[] | string | null;
};

export type SearchCombinedSiteMetadata = {
	isRovoEnabled?: boolean;
	isJiraIssueIndexed?: boolean;
	applicationMode?: string | null;
	siteName?: string | null;
	orgId?: string | null;
	productEdition?: string | null;
	productInfo?: SearchCombinedSiteProductInfo[] | null;
};

export type SearchCombinedConfigPayload = {
	firstPartyProducts?: SearchCombinedFirstPartyProduct[];
	thirdPartyProducts?: SearchCombinedThirdPartyProduct[];
	intercomHmac?: string | null;
	siteMetadata?: SearchCombinedSiteMetadata | null;
};

type SearchCombinedConfigResponse = {
	search?: {
		configuration?: SearchCombinedConfigPayload | null;
	} | null;
};

export type CombinedSearchConfig = SearchCombinedConfigPayload;

export type CombinedSearchConfigProps = {
	cloudId: string;
	/**
	 * Only used on the legacy FG-off (`rovo_fps_use_rovo_config`) path, where the
	 * runtime cache key is `getCombinedConfigHash(userId, cloudId)`. On the FG-on
	 * path the cache key is hash(cloudId) and userId is ignored.
	 *
	 * Remove when cleaning up rovo_fps_use_rovo_config
	 */
	userId?: string | null;
	aggAbsoluteUrl?: string;
	onlyRefreshIfExpired?: boolean;
	expiration?: number;
	degradationCallback?: DegradationCallback;
	cacheAnalyticsCallback?: CacheAnalyticsCallback;
	returnExpired?: boolean;
	networkCallbacks?: NetworkCallbacks;
	headers?: Record<string, string>;
};

// Adapter-backed cache (host StorageManager → account-namespaced key
// `atlassian.<accountId>.frontend.search-combined-config`, mirrored into
// getRemoteLocalStorage for SSR).
//
// Used by the UNTOUCHED legacy FG-off path: `updateCombinedConfigCacheEntry`,
// `seedCombinedConfigCacheEntry`, and the `getCombinedSearchConfig` legacy
// branch, which all rely on the AAID-scoped key.
const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorCombined,
	update: updateCombinedCache,
	seed: seedCombinedCache,
} = promiseCache<SearchCombinedConfigPayload>({
	name: SEARCH_COMBINED_CONFIG,
	shouldUseStorageAdapter: true,
});

// Bare-key runtime cache (client-only, NOT account-namespaced, NOT SSR-visible).
// Writes the literal `search-combined-config` localStorage key.
//
// Used ONLY by the FG-on (`rovo_fps_use_rovo_config`) `useRovoConfig` runtime
// writers — `getCombinedSearchConfig` (FG-on branch), `seedBase…`, `updateBase…`.
const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorBaseRuntime,
	update: updateBaseRuntimeCache,
	seed: seedBaseRuntimeCache,
} = promiseCache<SearchCombinedConfigPayload>({
	name: SEARCH_COMBINED_CONFIG,
	shouldUseStorageAdapter: false,
});

export const updateCombinedConfigCacheEntry = (
	userId: string,
	cloudId: string,
	updater: (config: SearchCombinedConfigPayload) => SearchCombinedConfigPayload,
): Promise<boolean> => {
	const key = getCombinedConfigHash(userId, cloudId);
	return updateCombinedCache(key.toString(), updater);
};

export const seedCombinedConfigCacheEntry = ({
	userId,
	cloudId,
	config,
	expiration,
}: {
	userId?: string | null;
	cloudId: string;
	config: SearchCombinedConfigPayload;
	expiration?: number;
}): Promise<void> => {
	const key = getCombinedConfigHash(userId || '', cloudId);
	return seedCombinedCache(key.toString(), config, expiration);
};

/**
 * The inner cache key for the **base** combined-config runtime entry used by
 * `useRovoConfig`. Keyed on a hash of the `cloudId` only — no `userId` — so the
 * entry is per-site but not per-account (the localStorage key prefix already
 * scopes per-account).
 */
const getBaseCombinedConfigCacheKey = (cloudId: string): string => generateHash(cloudId).toString();

/**
 * Resolve the runtime combined-config cache key, gated by the
 * `rovo_fps_use_rovo_config` feature gate.
 *
 * - **FG ON** (the canonical `useRovoConfig` path): a hash of the `cloudId`
 *   only — no `userId` in the inner key.
 * - **FG OFF** (the legacy `useFetchConfigLegacy` path): the historical
 *   `getCombinedConfigHash(userId, cloudId)` inner key, preserving the
 *   pre-RAOC cache behaviour byte-for-byte.
 */
const resolveRuntimeCombinedConfigCacheKey = (userId?: string | null, cloudId?: string): string =>
	fg('rovo_fps_use_rovo_config')
		? getBaseCombinedConfigCacheKey(cloudId || '')
		: getCombinedConfigHash(userId || '', cloudId || '').toString();

/**
 * Update the **base** combined-config cache entry (the runtime entry used by
 * `useRovoConfig`). Keyed on a hash of the `cloudId` only — no `userId`.
 *
 * Only used on the FG-on (`rovo_fps_use_rovo_config`) path.
 */
export const updateBaseCombinedConfigCacheEntry = (
	cloudId: string,
	updater: (config: SearchCombinedConfigPayload) => SearchCombinedConfigPayload,
): Promise<boolean> => updateBaseRuntimeCache(getBaseCombinedConfigCacheKey(cloudId), updater);

/**
 * Seed the **base** combined-config cache entry (the runtime entry used by
 * `useRovoConfig`). Keyed on a hash of the `cloudId` only — no `userId`.
 *
 * Only used on the FG-on (`rovo_fps_use_rovo_config`) path.
 */
export const seedBaseCombinedConfigCacheEntry = ({
	cloudId,
	config,
	expiration,
}: {
	cloudId: string;
	config: SearchCombinedConfigPayload;
	expiration?: number;
}): Promise<void> =>
	seedBaseRuntimeCache(getBaseCombinedConfigCacheKey(cloudId), config, expiration);

export const getCombinedSearchConfig = async ({
	cloudId,
	userId,
	aggAbsoluteUrl,
	onlyRefreshIfExpired,
	expiration,
	degradationCallback = () => {},
	cacheAnalyticsCallback,
	returnExpired,
	networkCallbacks,
	headers,
}: CombinedSearchConfigProps): Promise<SearchCombinedConfigPayload> => {
	// FG-on (`useRovoConfig`): bare-key runtime cache (client-only, no AAID).
	// FG-off (legacy): the original adapter-backed cache — untouched.
	const refreshOnAccess = fg('rovo_fps_use_rovo_config')
		? refreshOnAccessDecoratorBaseRuntime
		: refreshOnAccessDecoratorCombined;

	return refreshOnAccess({
		key: resolveRuntimeCombinedConfigCacheKey(userId, cloudId),
		supplier: () => {
			const combinedConfigPromise = fetchAgg<
				CombinedConfigQueryVariables,
				SearchCombinedConfigResponse
			>({
				graphQuery: getSearchConfigQuery(),
				variables: { cloudId },
				operationName: GQL_OPERATION_NAME,
				aggAbsoluteUrl,
				headers,
			}).then((response) => {
				const hasErrors = response.errors?.some((error) => error);
				if (hasErrors) {
					const errorsAsString = response.errors?.map((error) => error.message).join(', ');
					throw new Error(`Search combined config response had errors: ${errorsAsString}`);
				}

				const configuration = response.data?.search?.configuration;
				if (!configuration) {
					throw new Error('Search combined config response missing configuration.');
				}

				return configuration;
			});

			const degrader = new DegradablePromise<SearchCombinedConfigPayload>(
				combinedConfigPromise,
				degradationCallback,
			);

			return degrader.promise();
		},
		onlyRefreshIfExpired,
		expiration,
		returnExpired,
		analyticsCallback: cacheAnalyticsCallback,
		networkCallbacks,
		serveResolvedDuringRefresh: true,
	});
};
